import { useEffect, useRef, useState, type ReactNode } from 'react';
import { messageOf } from '@truecare/api-client';
import { Alert, Button, DateInput, Field, useFocusTrap } from '@truecare/ui';

/**
 * Phase 5f-b → 5f-c — generic artifact upload modal.
 *
 * Originally shipped as <CredentialUploadModal> in 5f-b; renamed to
 * <ArtifactUploadModal> in 5f-c because the compliance section
 * consumes the same shape. The modal is artifact-type-agnostic — its
 * only inputs are file + optional dates.
 *
 * Opens when a row in `<CaregiverCredentialsSection>` or
 * `<CaregiverComplianceSection>` clicks Upload/Re-upload. Captures:
 *
 *   - file (mandatory; image/* or PDF; ≤20 MB)
 *   - issuedAt (optional date)
 *   - expiresAt (optional date — required for renewable types per
 *     the Phase 5e operator runbook, but enforced operator-side, not
 *     portal-side, so a caregiver can submit "I don't know" and the
 *     operator follows up)
 *
 * The externalReferenceNumber field (for NACC / CNO / VSC) lives in
 * the row itself (`<ArtifactRow>` extras slot) — the modal reads it
 * via the `prefilledReference` prop and the parent passes the row's
 * current value at click time. Modal does not re-edit the reference
 * (keeps state ownership clean: extras live in row, file + dates
 * live in modal).
 *
 * On submit: calls `onSubmit({ file, issuedAt, expiresAt })`; parent
 * runs the signed-URL → S3 PUT → record-credential flow.
 *
 * Modal pattern mirrors admin's <OverrideActionButton> + 14e-a-r2
 * (role="dialog" + aria-modal + Escape close + addEventListener for
 * the keydown cleanup).
 */

const ACCEPTED_MIME_TYPES = 'image/jpeg,image/png,image/webp,application/pdf';
const MAX_BYTES = 20 * 1024 * 1024;

export interface ArtifactUploadModalProps {
  open: boolean;
  /** Title rendered in the modal header; the parent provides the
   *  type label (e.g., "Upload NACC certificate"). */
  title: string;
  /** Whether to prompt for issuedAt + expiresAt. Some artifact types
   *  may not need them; per the credential schema both fields are
   *  optional. Defaults to true. */
  promptDates?: boolean;
  /** Read-only display of the externalReferenceNumber the caregiver
   *  entered on the row card — shown so they can sanity-check before
   *  submitting. null hides the line. */
  referenceDisplay?: string | null;
  /** Optional per-type helper line (e.g., "Accepted: driver's licence
   *  or passport." for Government ID). null/undefined hides it. */
  hint?: string | null;
  /** Optional fields rendered above the file input (doc 16 — the additional-
   *  certificates section's type picker + "Other" name). State stays with the
   *  parent; pair with `submitBlocked` while they're incomplete. */
  leadingFields?: ReactNode;
  /** Keep Upload disabled even with a file chosen (parent-owned fields invalid). */
  submitBlocked?: boolean;
  onSubmit: (input: {
    file: File;
    issuedAt: string | null;
    expiresAt: string | null;
  }) => Promise<void>;
  onCancel: () => void;
  /** Optional initial error to render (e.g., parent's upload failure). */
  error?: string | null;
}

export function ArtifactUploadModal({
  open,
  title,
  promptDates = true,
  referenceDisplay,
  hint,
  leadingFields,
  submitBlocked = false,
  onSubmit,
  onCancel,
  error,
}: ArtifactUploadModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [issuedAt, setIssuedAt] = useState<string>('');
  const [expiresAt, setExpiresAt] = useState<string>('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Reset local state every time the modal opens.
  useEffect(() => {
    if (open) {
      setFile(null);
      setIssuedAt('');
      setExpiresAt('');
      setLocalError(null);
      setSubmitting(false);
    }
  }, [open]);

  // Focus in on open, trap Tab, Escape-to-close, restore focus to the
  // trigger on close (H-F4). Replaces the prior Escape-only handler.
  useFocusTrap(panelRef, { active: open, onEscape: onCancel });

  if (!open) return null;

  const displayedError = localError ?? error ?? null;

  async function submit(): Promise<void> {
    if (file === null) {
      setLocalError('Choose a file to upload.');
      return;
    }
    if (file.size > MAX_BYTES) {
      setLocalError('File is over 20 MB. Please upload a smaller copy.');
      return;
    }
    setSubmitting(true);
    setLocalError(null);
    try {
      await onSubmit({
        file,
        issuedAt: issuedAt === '' ? null : issuedAt,
        expiresAt: expiresAt === '' ? null : expiresAt,
      });
    } catch (e) {
      setLocalError(messageOf(e, 'Upload failed. Try again.'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="artifact-upload-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4"
      data-testid="artifact-upload-modal"
    >
      <div className="w-full max-w-md rounded-md border border-border bg-cream p-6 max-h-[90vh] overflow-y-auto">
        <h2
          id="artifact-upload-modal-title"
          className="font-heading text-xl text-forest"
        >
          {title}
        </h2>

        {displayedError && (
          <Alert
            tone="danger"
            className="mt-3"
            data-testid="artifact-upload-modal-error"
          >
            {displayedError}
          </Alert>
        )}

        {referenceDisplay && (
          <p className="mt-3 text-xs text-ink-muted">
            Reference number: <span className="font-mono">{referenceDisplay}</span>
          </p>
        )}

        {hint && (
          <p className="mt-3 text-xs text-ink-muted" data-testid="artifact-upload-modal-hint">
            {hint}
          </p>
        )}

        {leadingFields && <div className="mt-4 flex flex-col gap-4">{leadingFields}</div>}

        <Field
          label="Document (JPEG, PNG, WebP, or PDF — max 20 MB)"
          className="mt-4"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_MIME_TYPES}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="block w-full text-sm text-ink"
            data-testid="artifact-upload-modal-file"
          />
        </Field>

        {promptDates && (
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Issued on (optional)" hint="YYYY-MM-DD">
              <DateInput
                value={issuedAt}
                onChange={setIssuedAt}
                data-testid="artifact-upload-modal-issued-at"
              />
            </Field>
            <Field label="Expires on (optional)" hint="YYYY-MM-DD">
              <DateInput
                value={expiresAt}
                onChange={setExpiresAt}
                data-testid="artifact-upload-modal-expires-at"
              />
            </Field>
          </div>
        )}

        <div className="mt-5 flex items-center justify-end gap-3">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            disabled={file === null || submitting || submitBlocked}
            loading={submitting}
            onClick={() => void submit()}
            data-testid="artifact-upload-modal-submit"
          >
            Upload
          </Button>
        </div>
      </div>
    </div>
  );
}
